/**
 * Valorar lo que el bot contestó, y enseñarle (GKTIBO-115).
 *
 * El PO, 2026-09-23: _«sería bueno que yo le escriba una lección, no
 * literalmente lo que debería decir (…) y también poder valorar el mensaje,
 * una manito de bien y una de mal»_.
 *
 * ## Por qué una lección y no una respuesta
 *
 * Lo que el dueño quiere decir casi nunca es una FAQ: «no prometas domicilio
 * en toda la ciudad», «eso de la garantía te lo inventaste», «faltó decir que
 * hay que pedir turno». Son **instrucciones**.
 *
 * La versión anterior (GKTIBO-77) pedía «qué debería haber contestado» y
 * guardaba eso literal. El resultado se vio en el `.md` de un tenant: quedó
 * guardada una cortesía —«Ok perfecto, muchas gracias» → «Con gusto, estamos
 * para atenderte»— que **no es conocimiento del negocio** y que la búsqueda
 * semántica va a encontrar igual.
 *
 * ## Por qué ahora el modelo puede tocar el texto
 *
 * Aquella tarea lo prohibía para que una invención no llegara al `.md` en
 * silencio. El objetivo era bueno y el mecanismo equivocado: lo que protege el
 * conocimiento **no es prohibir el modelo**, es que nadie escriba sin que el
 * dueño lea el texto final. Eso ya estaba decidido el 2026-09-19 y vive en
 * `TarjetaDeConfirmacion`.
 *
 * Acá se usa el mismo contrato: propone → el dueño lee → aprueba → se escribe.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { type TipoDeCorreccion, api } from '../../lib/api.ts';
import { probar as t } from './textos-probar';

interface Propuesta {
  /** Sólo si es conocimiento: la sección a la que va. */
  tema?: string;
  texto: string;
}

const TIPOS: TipoDeCorreccion[] = ['conocimiento', 'comportamiento', 'defecto'];

/**
 * Qué se guarda según el tipo elegido (ADR-031 D3, GKTIBO-146).
 *
 * Lo que el modelo propuso vale sólo para el tipo con el que lo propuso. Si
 * el dueño cambia el tipo a mano, lo que se guarda es SU lección tal cual:
 * una propuesta pensada para una sección no es una regla, ni al revés.
 */
function propuestasPara(
  tipo: TipoDeCorreccion,
  delModelo: { tipo: TipoDeCorreccion; propuestas: Propuesta[] },
  leccion: string,
): Propuesta[] {
  if (tipo === delModelo.tipo && delModelo.propuestas.length > 0) return delModelo.propuestas;
  if (tipo === 'conocimiento') return [{ tema: 'otros', texto: leccion }];
  return [{ texto: leccion }];
}

function destinoDe(tipo: TipoDeCorreccion, propuestas: Propuesta[]): string {
  if (tipo === 'comportamiento') return t.destinoComportamiento;
  if (tipo === 'defecto') return t.destinoDefecto;
  const secciones = [...new Set(propuestas.map((p) => t.seccionDe[p.tema ?? 'otros'] ?? p.tema))];
  return t.destinoConocimiento(secciones.join('», «'));
}

export interface ValorarRespuestaProps {
  tenantId: string;
  /** Lo que el dueño preguntó y salió mal. Va tal cual. */
  pregunta: string;
  /** Lo que el bot contestó. Le da contexto a la lección. */
  respuesta: string;
}

type Paso = 'valorar' | 'escribiendo' | 'aprobando' | 'guardado' | 'gracias';

export function ValorarRespuesta({ tenantId, pregunta, respuesta }: ValorarRespuestaProps) {
  const queryClient = useQueryClient();
  const [paso, setPaso] = useState<Paso>('valorar');
  const [leccion, setLeccion] = useState('');
  const [delModelo, setDelModelo] = useState<{ tipo: TipoDeCorreccion; propuestas: Propuesta[] }>({
    tipo: 'conocimiento',
    propuestas: [],
  });
  const [tipo, setTipo] = useState<TipoDeCorreccion>('conocimiento');
  const [sinAsistente, setSinAsistente] = useState(false);
  const propuestas = propuestasPara(tipo, delModelo, leccion.trim());
  const [error, setError] = useState<string | null>(null);

  const proponer = useMutation({
    mutationFn: (texto: string) =>
      api.tenants.enseñar(tenantId, { pregunta, respuesta, leccion: texto }),
    onSuccess: (r) => {
      setError(null);
      setDelModelo({ tipo: r.tipo ?? 'conocimiento', propuestas: r.propuestas });
      setTipo(r.tipo ?? 'conocimiento');
      setSinAsistente(r.sinAsistente);
      setPaso('aprobando');
    },
    // El texto NO se borra: perder una lección escrita a mano es la clase de
    // detalle que hace que nadie vuelva a corregir nada.
    onError: () => setError(t.leccionNoSePudo),
  });

  const guardar = useMutation({
    mutationFn: async () => {
      // Cada propuesta es un hecho, con su tipo. Un defecto no es
      // conocimiento: se anota con la lección tal cual, para nosotros.
      for (const p of propuestas) {
        await api.tenants.guardarCorreccion(tenantId, {
          tipo,
          ...(tipo === 'conocimiento' ? { tema: p.tema ?? 'otros' } : {}),
          texto: p.texto,
          pregunta,
          respuesta,
        });
      }
    },
    onSuccess: () => {
      setPaso('guardado');
      // El conocimiento cambió: la lista de temas, la existencia del .md y el
      // estado del alta (pasa a `unsynced` hasta que se re-indexe). Sin esto,
      // el panel de la izquierda quedaba viejo hasta recargar.
      for (const clave of ['conocimiento-temas', 'tenant-kb-existe', 'onboarding-status']) {
        void queryClient.invalidateQueries({ queryKey: [clave, tenantId] });
      }
    },
    onError: () => setError(t.leccionNoSePudo),
  });

  if (paso === 'gracias') {
    return (
      <p className="probar-origen es-corregido" data-testid="probar-valorado-bien">
        {t.graciasPorValorar}
      </p>
    );
  }

  if (paso === 'guardado') {
    return (
      <p className="probar-origen es-corregido" data-testid="probar-correccion-guardada">
        {tipo === 'defecto' ? t.defectoAnotado : t.corregirGuardada}
      </p>
    );
  }

  if (paso === 'valorar') {
    return (
      <div className="probar-valorar">
        <button
          type="button"
          aria-label={t.respondioBien}
          title={t.respondioBien}
          // La manito de bien NO escribe nada: «estuvo bien» no es
          // conocimiento nuevo. Sirve para saber qué no tocar.
          onClick={() => setPaso('gracias')}
        >
          <ThumbsUp size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={t.respondioMal}
          title={t.respondioMal}
          onClick={() => setPaso('escribiendo')}
        >
          <ThumbsDown size={14} aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (paso === 'aprobando') {
    return (
      <div className="probar-correccion" data-testid="probar-propuesta">
        {sinAsistente && (
          <p className="probar-aviso" data-testid="probar-sin-asistente" role="note">
            {t.leccionSinAsistente}
          </p>
        )}
        {/* El tipo decide el destino, y el dueño lo ve y lo puede cambiar
            ANTES de guardar (ADR-031 D3). */}
        <label htmlFor="probar-tipo">{t.destinoTitulo}</label>
        <select
          id="probar-tipo"
          data-testid="probar-tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoDeCorreccion)}
        >
          {TIPOS.map((opcion) => (
            <option key={opcion} value={opcion}>
              {opcion === 'conocimiento'
                ? t.tipoConocimiento
                : opcion === 'comportamiento'
                  ? t.tipoComportamiento
                  : t.tipoDefecto}
            </option>
          ))}
        </select>
        <p className="probar-correccion-ayuda" data-testid="probar-destino">
          {destinoDe(tipo, propuestas)}
        </p>

        {tipo !== 'defecto' && <p className="probar-correccion-ayuda">{t.leccionEstoSeGuarda}</p>}

        {propuestas.map((p) => (
          <p key={`${p.tema ?? 'regla'}-${p.texto}`} className="probar-propuesta-texto">
            {p.texto}
          </p>
        ))}

        {error && (
          <p className="alta-chat-error" role="alert">
            {error}
          </p>
        )}

        <div className="probar-correccion-acciones">
          <Button type="button" loading={guardar.isPending} onClick={() => guardar.mutate()}>
            {tipo === 'defecto' ? t.anotarDefecto : t.corregirGuardar}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setPaso('escribiendo')}>
            {t.leccionVolverAEscribir}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="probar-correccion"
      onSubmit={(e) => {
        e.preventDefault();
        const texto = leccion.trim();
        // Una lección vacía no enseña nada y pisaría el conocimiento con aire.
        if (!texto || proponer.isPending) return;
        proponer.mutate(texto);
      }}
    >
      <label htmlFor="probar-leccion-texto">{t.leccionTitulo}</label>
      <p className="probar-correccion-ayuda">{t.leccionAyuda}</p>
      <textarea
        id="probar-leccion-texto"
        data-testid="probar-leccion-texto"
        value={leccion}
        rows={3}
        placeholder={t.leccionEjemplo}
        onChange={(e) => setLeccion(e.target.value)}
      />

      {error && (
        <p className="alta-chat-error" role="alert">
          {error}
        </p>
      )}

      <div className="probar-correccion-acciones">
        <Button type="submit" loading={proponer.isPending}>
          {t.leccionVerQueGuardar}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setPaso('valorar')}>
          {t.corregirCancelar}
        </Button>
      </div>
    </form>
  );
}
